import { Formik, FormikHelpers } from "formik";
import {
  SharedserviceMelcoModelsAlphabetCollection,
  SharedserviceMelcoModelsAlphabetListItem,
  useFormSubmit,
  Drawer as DrawerType,
  useAuthenticatedAPIConfig,
  UseApiQueryKey,
  AlphabetCollectionsDraftApi,
  AlphabetCollectionsDraftApiUserAlphabetCollectionUpdateDraftRequest,
} from "melco-shared-logic";
import { QueryObserverResult, useQueryClient } from "react-query";
import { SubmitAction } from "../../header/SavePublishHeader";
import { DirtyFormPrompt } from "../../form/DirtyFormPrompt";
import { DisplayFontCollectionDetailForm } from "./DisplayFontCollectionDetailForm";
import { queryKeyForFont } from "../../../hooks/libraries/fonts/useFont";
import { ALL_PUBLISHED_STATES } from "../../../helper/publishedState";

type FontCollectionDetailFormProps = {
  fontCollection: SharedserviceMelcoModelsAlphabetCollection;
  refetch: () => Promise<
    QueryObserverResult<SharedserviceMelcoModelsAlphabetCollection, unknown>
  >;
  referencesDrawer: DrawerType;
  queryKey: UseApiQueryKey;
};

type FontCollectionDetailFormValueType = {
  id: string;
  name: string;
  alphabet_list: SharedserviceMelcoModelsAlphabetListItem[];
  submitAction: SubmitAction;
};

export const fontCollectionToInitialValues = (
  fontCollection: SharedserviceMelcoModelsAlphabetCollection
) => {
  const { id, name, alphabet_list } = fontCollection;

  const initialValues: FontCollectionDetailFormValueType = {
    id: id!,
    name: name ?? "",
    alphabet_list: alphabet_list ?? [],
    submitAction: "save",
  };

  return initialValues;
};

export const FontCollectionDetailForm: React.FC<
  FontCollectionDetailFormProps
> = ({ fontCollection, refetch, referencesDrawer, queryKey }) => {
  const queryClient = useQueryClient();

  const authenticatedApiConfig = useAuthenticatedAPIConfig();

  const { id } = fontCollection;

  const onSubmit = async (
    values: FontCollectionDetailFormValueType,
    formikHelpers: FormikHelpers<FontCollectionDetailFormValueType>
  ) => {
    const { submitAction } = values;

    if (submitAction === "publish") {
      await onPublish(values, formikHelpers, { isManuallyResettingForm: true });
    } else {
      await onSave(values, formikHelpers, { isManuallyResettingForm: true });
    }
  };

  const handleSaveAndPublish = async (
    values: FontCollectionDetailFormValueType,
    formikHelpers: FormikHelpers<FontCollectionDetailFormValueType>,
    options: {
      publish: boolean;
    }
  ) => {
    const { publish } = options;
    const { name, alphabet_list } = values;

    const api = new AlphabetCollectionsDraftApi(authenticatedApiConfig());

    const entityAttributes: AlphabetCollectionsDraftApiUserAlphabetCollectionUpdateDraftRequest =
    {
      id: id!,
      sharedserviceMelcoModelsUpdateAlphabetCollection: {
        name,
        alphabet_list,
      },
    };

    let newEntity = await api.userAlphabetCollectionUpdateDraft(
      entityAttributes
    );

    if (publish) {
      newEntity = await api.userAlphabetCollectionPublish({
        id: id!,
      });
    }

    // update ui with new data
    formikHelpers.resetForm({
      values: fontCollectionToInitialValues(newEntity),
    });
    queryClient.setQueryData(queryKey, newEntity);

    return {
      successMessages: [
        {
          message: "default",
        },
      ],
    };
  };

  const formSubmitWithPublish = (publish: boolean) => {
    return [
      async (
        values: FontCollectionDetailFormValueType,
        formikHelpers: FormikHelpers<FontCollectionDetailFormValueType>
      ) => {
        return handleSaveAndPublish(values, formikHelpers, {
          publish,
        });
      },
      {
        translationPrefix: [
          "libraries.font_collections.detail",
          publish ? "publish" : "save",
        ].join("."),
        onError: () => {
          // refetch entity from server
          queryClient.invalidateQueries(
            queryKeyForFont(id!, ALL_PUBLISHED_STATES)
          );
        },
      },
    ] as const;
  };

  const onSave = useFormSubmit(...formSubmitWithPublish(false));
  const onPublish = useFormSubmit(...formSubmitWithPublish(true));

  const initialValues = fontCollectionToInitialValues(fontCollection);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnBlur={false}
      validateOnChange={false}
    >
      <DirtyFormPrompt>
        <DisplayFontCollectionDetailForm
          fontCollection={fontCollection}
          refetch={refetch}
          referencesDrawer={referencesDrawer}
        />
      </DirtyFormPrompt>
    </Formik>
  );
};
